/**
 * Topbar — the slim header above each staff screen (UI/UX refresh, Phase 2).
 *
 * Shows a breadcrumb-style page title on the left, and the primary
 * "New Quote" action plus sign-out on the right. Sits inside AppLayout,
 * beside the Sidebar.
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore';
import { STAFF_ADMIN } from '../../store/roleCohorts';
import { useQuoteStore } from '../../store/useQuoteStore';
import Button from '../ui/Button';
import { dialog } from '../ui';

const TITLE_RULES: Array<{ match: (p: string) => boolean; label: string }> = [
  { match: (p) => p === '/staff/home', label: 'Dashboard' },
  { match: (p) => p === '/staff', label: 'New Quote' },
  { match: (p) => p.startsWith('/staff/payments'), label: 'Payments' },
  { match: (p) => p.startsWith('/staff/quotes'), label: 'Quotes' },
  { match: (p) => p.startsWith('/staff/orders'), label: 'Orders' },
  { match: (p) => p.startsWith('/staff/customers'), label: 'Customers' },
  { match: (p) => p.startsWith('/staff/vendors'), label: 'Vendors' },
  { match: (p) => p.startsWith('/staff/requisitions'), label: 'Purchase Requisitions' },
  { match: (p) => p.startsWith('/staff/purchase-orders'), label: 'Purchase Orders' },
  { match: (p) => p.startsWith('/staff/schedule'), label: 'Measurement Schedule' },
  { match: (p) => p.startsWith('/staff/deliveries'), label: 'Deliveries' },
  { match: (p) => p.startsWith('/staff/admin'), label: 'Administration' },
];

const titleFor = (pathname: string): string =>
  TITLE_RULES.find((r) => r.match(pathname))?.label ?? 'WSS Operations';

interface TopbarProps {
  /** Open the mobile navigation drawer (Sidebar). Mobile only. */
  onOpenMobileNav: () => void;
}

export default function Topbar({ onOpenMobileNav }: TopbarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { role, logout } = useAuthStore();
  // Roles that can create quotes — the New Quote button is hidden for
  // anyone else. Phase 0b removed STAFF (now baseline employee, claims-only)
  // from this set; Measurer, Finance, Finance Manager, and Purchaser have
  // never been able to.
  const canCreateQuotes = STAFF_ADMIN.includes(role);

  const handleNewQuote = async () => {
    const ok = await dialog.confirm({
      title: 'Start a new quote?',
      message: 'Any unsaved changes in your current workspace will be discarded.',
      confirmLabel: 'Start new',
    });
    if (!ok) return;
    useQuoteStore.getState().resetWorkspace();
    navigate('/staff');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/staff/login');
  };

  return (
    <header className="h-[60px] bg-surface border-b border-outline-variant flex items-center gap-3 px-4 sm:px-6 shrink-0 sticky top-0 z-30 print:hidden">
      {/* Hamburger — opens the Sidebar drawer on mobile. */}
      <button
        type="button"
        onClick={onOpenMobileNav}
        className="lg:hidden -ml-1 p-1.5 rounded-lg text-on-surface hover:bg-surface-container-highest"
        aria-label="Open navigation menu"
      >
        <span className="material-symbols-outlined">menu</span>
      </button>
      <div className="flex items-center gap-2 text-sm min-w-0">
        <span className="hidden sm:inline text-outline">WSS</span>
        <span className="hidden sm:inline material-symbols-outlined text-[16px] text-outline">
          chevron_right
        </span>
        <span className="font-semibold text-on-surface truncate">
          {titleFor(location.pathname)}
        </span>
      </div>

      <div className="flex-1" />

      {canCreateQuotes && (
        <Button
          variant="primary"
          onClick={() => void handleNewQuote()}
          icon={<span className="material-symbols-outlined text-[18px]">add_circle</span>}
        >
          New Quote
        </Button>
      )}
      <Button variant="secondary" onClick={() => void handleLogout()}>
        Sign out
      </Button>
    </header>
  );
}
